import React, { useState, useEffect, ChangeEvent, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    List,
    ListItem,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    CircularProgress,
    Typography,
    Box,
    Alert,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import { useAppDispatch } from "../../store/hook";
import { RootState } from "../../store/store";
import { setActiveConversation, findOrCreateConversation } from "../../store/slices/messagingSlice"; // Import action
import apiClient from "../../utils/apiClient"; // Use our API client
import { isTokenValid } from "../../utils/tokenUtils"; // Import token validation function

// Define a type for the User search result (adjust based on your actual API response)
interface UserSearchResult {
    userId: string; // Assuming the primary ID is userId from profile
    firstName: string;
    lastName: string;
    profilePhotoUrl?: string; // Optional profile photo
}

interface NewConversationModalProps {
    open: boolean;
    onClose: () => void;
    preselectedUserId?: string; // Optional: ID of the user to message directly
    preselectedUserName?: string; // Optional: Name of the preselected user
}

const NewConversationModal: React.FC<NewConversationModalProps> = ({
    open,
    onClose,
    preselectedUserId,
    preselectedUserName,
}) => {
    const dispatch = useAppDispatch();
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
    const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false); // Loading state for search
    const [searchError, setSearchError] = useState<string | null>(null);
    const [createStatus, setCreateStatus] = useState<"idle" | "pending" | "failed">("idle");
    const [createError, setCreateError] = useState<string | null>(null);
    const authState = useSelector((state: RootState) => state.auth); // Get auth state
    const navigate = useNavigate();

    // --- New State ---
    // Track if the modal is in preselected mode
    const [isPreselectedMode, setIsPreselectedMode] = useState(false);
    // Store the name for display in preselected mode
    const [recipientName, setRecipientName] = useState<string | null>(null);

    // Debounce timer reference
    const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // --- Handle User Selection ---
    const handleUserSelect = (userId: string) => {
        console.log(`[NewConversationModal] User selected: ${userId}`);
        setSelectedUserId(userId);
        // Optionally clear search query/results after selection if desired
        // setSearchQuery('');
        // setSearchResults([]);
    };

    // --- Reset state when modal closes ---
    const handleModalClose = useCallback(() => {
        setSearchQuery("");
        setSearchResults([]);
        setSelectedUserId(null);
        setIsLoading(false);
        setSearchError(null);
        setCreateStatus("idle");
        setCreateError(null);
        // --- Reset new state ---
        setIsPreselectedMode(false); // Reset mode flag
        setRecipientName(null); // Reset recipient name
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current); // Clear debounce on close
        }
        onClose(); // Call the parent's onClose handler
    }, [onClose]);

    // --- Handle Start Conversation ---
    const handleStartConversation = useCallback(async () => {
        if (!selectedUserId) return;

        // Additional debug logging
        const token = localStorage.getItem("accessToken");
        // console.log("[DEBUG] Authentication check before conversation creation:");
        // console.log("- Token exists:", !!token);
        // console.log("- Selected user ID:", selectedUserId);

        // Additional auth check to be sure we're still authenticated
        const isLoggedIn = !!authState.token;
        const tokenValid = isTokenValid();
        // console.log("[NewConversationModal] Authentication check:", {
        //     isLoggedIn,
        //     tokenValid,
        //     stateAuthenticated: authState.isAuthenticated,
        //     hasStateToken: !!authState.token,
        // });

        setCreateStatus("pending");
        setCreateError(null);
        console.log(
            `[NewConversationModal] Attempting to create conversation with user: ${selectedUserId}`,
        );

        try {
            // Dispatch the findOrCreateConversation thunk
            const resultAction = await dispatch(
                findOrCreateConversation({ recipientId: selectedUserId }),
            );

            // Check if the thunk fulfilled successfully
            if (findOrCreateConversation.fulfilled.match(resultAction)) {
                const newConversation = resultAction.payload;
                console.log(
                    `[NewConversationModal] Conversation created successfully: ${newConversation.id}`,
                );
                // Set the new conversation as active in Redux
                dispatch(setActiveConversation(newConversation.id));

                // Navigate to the messaging page
                navigate("/messaging");
                handleModalClose(); // Close the modal on success
            } else if (findOrCreateConversation.rejected.match(resultAction)) {
                // Handle rejected case (error occurred)
                const payload = resultAction.payload as any; // Type assertion for easier access
                console.error("[NewConversationModal] Conversation find/create failed:", payload);

                // Check if it was a 409 Conflict (conversation already exists)
                if (payload?.status === 409 && payload?.existingConversationId) {
                    console.log(
                        `[NewConversationModal] Conversation already exists (ID: ${payload.existingConversationId}). Setting active and navigating.`,
                    );
                    // Set the existing conversation as active
                    dispatch(setActiveConversation(payload.existingConversationId));

                    // Navigate to the messaging page
                    navigate("/messaging");
                    handleModalClose(); // Close the modal
                } else {
                    // Handle other types of errors
                    setCreateError(payload?.message ?? "Failed to start or find conversation.");
                    setCreateStatus("failed");
                }
            } else {
                // Handle unexpected case where action is neither fulfilled nor rejected
                // (though this shouldn't happen with standard thunks)
                console.error(
                    "[NewConversationModal] Unexpected action result state:",
                    resultAction,
                );
                setCreateError("An unexpected error occurred.");
                setCreateStatus("failed");
            }
        } catch (error: any) {
            // Catch unexpected errors during dispatch itself
            console.error(
                "[NewConversationModal] Unexpected error during conversation creation:",
                error,
            );
            setCreateError(error.message || "An unexpected error occurred.");
            setCreateStatus("failed");
        }
        // Note: No 'succeeded' state set here, as we close the modal on success.
        // If it failed, 'pending' becomes 'failed'.
    }, [selectedUserId, dispatch, handleModalClose, authState, navigate]);

    // --- Effect to handle preselected user ---
    useEffect(() => {
        // Only run logic when the modal is open
        if (open) {
            if (preselectedUserId) {
                // If a user ID is preselected when opening:
                console.log(
                    `[NewConversationModal] Preselected mode activated for user ID: ${preselectedUserId}`,
                );

                // Log auth state for debugging
                const token = localStorage.getItem("accessToken");
                // console.log("[NewConversationModal] Authentication details:", {
                //     hasToken: !!token,
                //     tokenLength: token?.length || 0,
                //     isAuthenticatedState: authState.isAuthenticated,
                //     tokenFromState: !!authState.token,
                //     userId: authState.user?.id,
                // });

                // Only proceed if authenticated
                if (authState.isAuthenticated && authState.token) {
                    setSelectedUserId(preselectedUserId);
                    setIsPreselectedMode(true);
                    setRecipientName(preselectedUserName || "User");
                    setSearchQuery("");
                    setSearchResults([]);
                    setSearchError(null);
                    setIsLoading(false);

                    // Automatically start the conversation after a slight delay
                    const timer = setTimeout(() => {
                        handleStartConversation();
                    }, 100);

                    return () => clearTimeout(timer);
                } else {
                    // Set error state if not authenticated
                    setCreateStatus("failed");
                    setCreateError(
                        "You must be logged in to send messages. Please log in and try again.",
                    );
                    console.error(
                        "[NewConversationModal] Authentication required to message users",
                    );
                }
            } else {
                // Standard search mode
                setIsPreselectedMode(false);
                setSelectedUserId(null);
                setRecipientName(null);
            }
        }
    }, [
        open,
        preselectedUserId,
        preselectedUserName,
        handleStartConversation,
        authState,
        navigate,
    ]);

    // --- Effect to display loading state while auto-starting conversation ---
    useEffect(() => {
        if (isPreselectedMode && open) {
            setCreateStatus("pending");
        }
    }, [isPreselectedMode, open]);

    // --- Check Authentication ---
    useEffect(() => {
        if (open) {
            // Check if user is logged in
            const isLoggedIn = isTokenValid() && authState.isAuthenticated;

            // console.log("[NewConversationModal] Authentication check:", {
            //     isLoggedIn,
            //     tokenValid: isTokenValid(),
            //     stateAuthenticated: authState.isAuthenticated,
            //     hasStateToken: Boolean(authState.token),
            // });

            // If not logged in, set error immediately
            if (!isLoggedIn) {
                setCreateStatus("failed");
                setCreateError("You must be logged in to send messages. Please log in first.");
            }
        }
    }, [open, authState]);

    // --- Debounced Search Function ---
    const performSearch = useCallback(async (query: string) => {
        if (query.length < 2) {
            setSearchResults([]); // Clear results if query is too short
            setSearchError(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setSearchError(null);
        console.log(`[NewConversationModal] Searching for: ${query}`);

        try {
            const response = await apiClient.get<{ users: UserSearchResult[] }>(
                `/users/search?query=${encodeURIComponent(query)}`,
            );
            // console.log("[NewConversationModal] Search response:", response.data);
            setSearchResults(response.data.users || []); // Extract the 'users' array
        } catch (err: any) {
            console.error("[NewConversationModal] Search error:", err);
            // Determine a user-friendly error message
            const message = err.response?.data?.message || err.message || "Failed to search users";
            setSearchError(message);
            setSearchResults([]); // Clear results on error
        } finally {
            setIsLoading(false);
        }
    }, []); // Dependencies: apiClient (assumed stable)

    // --- Handle Search Input Change with Debouncing ---
    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const query = event.target.value;
        setSearchQuery(query);
        setSelectedUserId(null); // Reset selection when query changes

        // Clear the previous debounce timer if it exists
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        // Set a new timer to execute the search after 300ms
        debounceTimeoutRef.current = setTimeout(() => {
            performSearch(query);
        }, 300); // 300ms debounce delay
    };

    // Cleanup debounce timer on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    return (
        <Dialog open={open} onClose={handleModalClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                {
                    isPreselectedMode && recipientName
                        ? `Message ${recipientName}` // Show recipient name if in preselected mode
                        : "Start New Conversation" // Default title for search mode
                }
            </DialogTitle>
            <DialogContent dividers>
                {/* --- Conditionally Render Search Elements --- */}
                {/* Only show search input and results if NOT in preselected mode */}
                {!isPreselectedMode && (
                    <>
                        {/* Search Input */}
                        <TextField
                            autoFocus // Focus input when modal opens
                            margin="dense"
                            id="user-search"
                            label="Search users..."
                            type="search"
                            fullWidth
                            variant="outlined"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            InputProps={{
                                endAdornment: isLoading ? <CircularProgress size={20} /> : null,
                            }}
                        />

                        {/* Search Error Display */}
                        {searchError && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                {searchError}
                            </Alert>
                        )}

                        {/* Search Results List */}
                        <Box sx={{ maxHeight: "300px", overflowY: "auto", mt: 1 }}>
                            {searchResults.length > 0 && (
                                <List dense>
                                    {searchResults.map((user) => (
                                        <ListItem key={user.userId} disablePadding>
                                            <ListItemButton
                                                selected={selectedUserId === user.userId}
                                                onClick={() => handleUserSelect(user.userId)}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar src={user.profilePhotoUrl}>
                                                        {" "}
                                                        {/* Use photo URL */}
                                                        {!user.profilePhotoUrl && (
                                                            <PersonIcon />
                                                        )}{" "}
                                                        {/* Fallback icon */}
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={`${user.firstName} ${user.lastName}`}
                                                />
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleModalClose} color="primary">
                    Cancel
                </Button>
                <Button
                    onClick={handleStartConversation}
                    color="primary"
                    disabled={!selectedUserId || createStatus === "pending"}
                >
                    {createStatus === "pending" ? (
                        <CircularProgress size={20} />
                    ) : (
                        "Start Conversation"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NewConversationModal;
